import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Permit {
  id: number;
  permit_number: string | null;
  permit_type: string;
  authority: string | null;
  status: 'pending' | 'submitted' | 'approved' | 'expired' | 'denied';
  applied_date: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  fee: number | null;
  notes: string | null;
  inspection_count: number;
}

interface Inspection {
  id: number;
  permit_id: number | null;
  permit_type: string | null;
  permit_number: string | null;
  inspection_type: string;
  inspector_name: string | null;
  scheduled_date: string | null;
  completed_date: string | null;
  result: 'pending' | 'passed' | 'failed' | 'conditional';
  notes: string | null;
}

interface PermitsTabProps {
  jobId: string | number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const money = (n: number | null) =>
  n == null ? '—' : `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isExpiringWithin30Days = (expiry: string | null): boolean => {
  if (!expiry) return false;
  const diff = new Date(expiry).getTime() - Date.now();
  return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000;
};

const isExpired = (expiry: string | null): boolean => {
  if (!expiry) return false;
  return new Date(expiry).getTime() < Date.now();
};

const permitStatusBadge: Record<Permit['status'], string> = {
  pending: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  expired: 'bg-orange-100 text-orange-700',
  denied: 'bg-red-100 text-red-700',
};

const inspectionResultBadge: Record<Inspection['result'], string> = {
  pending: 'bg-gray-100 text-gray-600',
  passed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  conditional: 'bg-orange-100 text-orange-700',
};

const inputCls =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60';

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

interface DeleteModalProps {
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

const DeleteModal = ({ label, onConfirm, onCancel, deleting }: DeleteModalProps) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={e => { if (e.target === e.currentTarget && !deleting) onCancel(); }}
  >
    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
          <span className="material-symbols-outlined text-xl">delete</span>
        </span>
        <h2 className="text-base font-bold text-gray-900">Delete {label}</h2>
      </div>
      <p className="text-sm text-gray-600">
        Are you sure you want to delete this {label.toLowerCase()}? This cannot be undone.
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ── Permit Form ───────────────────────────────────────────────────────────────

interface PermitFormData {
  permit_type: string;
  permit_number: string;
  authority: string;
  status: Permit['status'];
  applied_date: string;
  issued_date: string;
  expiry_date: string;
  fee: string;
  notes: string;
}

const emptyPermitForm = (): PermitFormData => ({
  permit_type: '',
  permit_number: '',
  authority: '',
  status: 'pending',
  applied_date: '',
  issued_date: '',
  expiry_date: '',
  fee: '',
  notes: '',
});

const permitFormFromData = (p: Permit): PermitFormData => ({
  permit_type: p.permit_type,
  permit_number: p.permit_number ?? '',
  authority: p.authority ?? '',
  status: p.status,
  applied_date: p.applied_date ? p.applied_date.slice(0, 10) : '',
  issued_date: p.issued_date ? p.issued_date.slice(0, 10) : '',
  expiry_date: p.expiry_date ? p.expiry_date.slice(0, 10) : '',
  fee: p.fee != null ? String(p.fee) : '',
  notes: p.notes ?? '',
});

interface PermitFormProps {
  initial?: Permit;
  jobId: number;
  onSaved: () => void;
  onCancel: () => void;
}

const PermitForm = ({ initial, jobId, onSaved, onCancel }: PermitFormProps) => {
  const [form, setForm] = useState<PermitFormData>(
    initial ? permitFormFromData(initial) : emptyPermitForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field: keyof PermitFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.permit_type.trim()) { setError('Permit type is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        permit_type: form.permit_type.trim(),
        permit_number: form.permit_number.trim() || undefined,
        authority: form.authority.trim() || undefined,
        status: form.status,
        applied_date: form.applied_date || undefined,
        issued_date: form.issued_date || undefined,
        expiry_date: form.expiry_date || undefined,
        fee: form.fee ? parseFloat(form.fee) : undefined,
        notes: form.notes.trim() || undefined,
      };
      if (initial) {
        await apiClient.patch(`/jobs/${jobId}/permits/${initial.id}`, payload);
      } else {
        await apiClient.post(`/jobs/${jobId}/permits`, payload);
      }
      onSaved();
    } catch {
      setError('Failed to save permit.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-bold text-gray-900">
        {initial ? 'Edit Permit' : 'Add Permit'}
      </h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label required>Permit Type</Label>
          <input
            type="text"
            value={form.permit_type}
            onChange={set('permit_type')}
            disabled={saving}
            placeholder="e.g. Building, Electrical, Plumbing"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Permit Number</Label>
          <input
            type="text"
            value={form.permit_number}
            onChange={set('permit_number')}
            disabled={saving}
            placeholder="Issuing authority's number"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Issuing Authority</Label>
          <input
            type="text"
            value={form.authority}
            onChange={set('authority')}
            disabled={saving}
            placeholder="e.g. District of North Cowichan"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Status</Label>
          <select value={form.status} onChange={set('status')} disabled={saving} className={inputCls}>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="expired">Expired</option>
            <option value="denied">Denied</option>
          </select>
        </div>
        <div>
          <Label>Applied Date</Label>
          <input type="date" value={form.applied_date} onChange={set('applied_date')} disabled={saving} className={inputCls} />
        </div>
        <div>
          <Label>Issued Date</Label>
          <input type="date" value={form.issued_date} onChange={set('issued_date')} disabled={saving} className={inputCls} />
        </div>
        <div>
          <Label>Expiry Date</Label>
          <input type="date" value={form.expiry_date} onChange={set('expiry_date')} disabled={saving} className={inputCls} />
        </div>
        <div>
          <Label>Fee ($)</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.fee}
            onChange={set('fee')}
            disabled={saving}
            placeholder="0.00"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <Label>Notes</Label>
        <textarea
          value={form.notes}
          onChange={set('notes')}
          disabled={saving}
          rows={2}
          placeholder="Any additional notes…"
          className={`${inputCls} resize-none`}
        />
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Permit'}
        </button>
      </div>
    </form>
  );
};

// ── Permit Row ────────────────────────────────────────────────────────────────

interface PermitRowProps {
  permit: Permit;
  jobId: number;
  onEditRequest: (p: Permit) => void;
  onDeleteRequest: (id: number) => void;
}

const PermitRow = ({ permit, jobId, onEditRequest, onDeleteRequest }: PermitRowProps) => {
  const expiring = isExpiringWithin30Days(permit.expiry_date);
  const expired = isExpired(permit.expiry_date);

  // Suppress unused param lint — jobId is passed for future inline use
  void jobId;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-gray-900">{permit.permit_type}</p>
            {permit.permit_number && (
              <span className="text-xs font-mono text-gray-400">{permit.permit_number}</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${permitStatusBadge[permit.status]}`}>
              {permit.status}
            </span>
          </div>
          {permit.authority && (
            <p className="text-xs text-gray-500 mt-0.5">{permit.authority}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onEditRequest(permit)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base leading-none">edit</span>
            Edit
          </button>
          <button
            onClick={() => onDeleteRequest(permit.id)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base leading-none">delete</span>
            Delete
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <div>
          <p className="text-xs text-gray-400">Applied</p>
          <p className="text-xs font-medium text-gray-700">{fmtDate(permit.applied_date)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Issued</p>
          <p className="text-xs font-medium text-gray-700">{fmtDate(permit.issued_date)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Expires</p>
          <p className={`text-xs font-medium ${
            expired ? 'text-red-600' : expiring ? 'text-orange-600' : 'text-gray-700'
          }`}>
            {fmtDate(permit.expiry_date)}
            {expiring && !expired && (
              <span className="ml-1 inline-flex items-center gap-0.5">
                <span className="material-symbols-outlined text-xs leading-none">warning</span>
                soon
              </span>
            )}
            {expired && permit.expiry_date && (
              <span className="ml-1 inline-flex items-center gap-0.5">
                <span className="material-symbols-outlined text-xs leading-none">error</span>
                expired
              </span>
            )}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Fee</p>
          <p className="text-xs font-medium text-gray-700">{money(permit.fee)}</p>
        </div>
      </div>

      {permit.inspection_count > 0 && (
        <p className="text-xs text-gray-400 mt-2">
          {permit.inspection_count} inspection{permit.inspection_count !== 1 ? 's' : ''} linked
        </p>
      )}

      {permit.notes && (
        <p className="text-xs text-gray-500 mt-2 italic">{permit.notes}</p>
      )}
    </div>
  );
};

// ── Inspection Form ───────────────────────────────────────────────────────────

interface InspectionFormData {
  inspection_type: string;
  permit_id: string;
  inspector_name: string;
  scheduled_date: string;
  completed_date: string;
  result: Inspection['result'];
  notes: string;
}

const emptyInspectionForm = (): InspectionFormData => ({
  inspection_type: '',
  permit_id: '',
  inspector_name: '',
  scheduled_date: '',
  completed_date: '',
  result: 'pending',
  notes: '',
});

const inspectionFormFromData = (i: Inspection): InspectionFormData => ({
  inspection_type: i.inspection_type,
  permit_id: i.permit_id != null ? String(i.permit_id) : '',
  inspector_name: i.inspector_name ?? '',
  scheduled_date: i.scheduled_date ? i.scheduled_date.slice(0, 10) : '',
  completed_date: i.completed_date ? i.completed_date.slice(0, 10) : '',
  result: i.result,
  notes: i.notes ?? '',
});

interface InspectionFormProps {
  initial?: Inspection;
  jobId: number;
  permits: Permit[];
  onSaved: () => void;
  onCancel: () => void;
}

const InspectionForm = ({ initial, jobId, permits, onSaved, onCancel }: InspectionFormProps) => {
  const [form, setForm] = useState<InspectionFormData>(
    initial ? inspectionFormFromData(initial) : emptyInspectionForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field: keyof InspectionFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.inspection_type.trim()) { setError('Inspection type is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        inspection_type: form.inspection_type.trim(),
        permit_id: form.permit_id ? parseInt(form.permit_id, 10) : undefined,
        inspector_name: form.inspector_name.trim() || undefined,
        scheduled_date: form.scheduled_date || undefined,
        completed_date: form.completed_date || undefined,
        result: form.result,
        notes: form.notes.trim() || undefined,
      };
      if (initial) {
        await apiClient.patch(`/jobs/${jobId}/inspections/${initial.id}`, payload);
      } else {
        await apiClient.post(`/jobs/${jobId}/inspections`, payload);
      }
      onSaved();
    } catch {
      setError('Failed to save inspection.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-bold text-gray-900">
        {initial ? 'Edit Inspection' : 'Add Inspection'}
      </h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label required>Inspection Type</Label>
          <input
            type="text"
            value={form.inspection_type}
            onChange={set('inspection_type')}
            disabled={saving}
            placeholder="e.g. Framing, Foundation, Final"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Linked Permit</Label>
          <select value={form.permit_id} onChange={set('permit_id')} disabled={saving} className={inputCls}>
            <option value="">— None —</option>
            {permits.map(p => (
              <option key={p.id} value={p.id}>
                {p.permit_type}{p.permit_number ? ` (${p.permit_number})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Inspector Name</Label>
          <input
            type="text"
            value={form.inspector_name}
            onChange={set('inspector_name')}
            disabled={saving}
            placeholder="Inspector's name"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Result</Label>
          <select value={form.result} onChange={set('result')} disabled={saving} className={inputCls}>
            <option value="pending">Pending</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="conditional">Conditional</option>
          </select>
        </div>
        <div>
          <Label>Scheduled Date</Label>
          <input type="date" value={form.scheduled_date} onChange={set('scheduled_date')} disabled={saving} className={inputCls} />
        </div>
        <div>
          <Label>Completed Date</Label>
          <input type="date" value={form.completed_date} onChange={set('completed_date')} disabled={saving} className={inputCls} />
        </div>
      </div>

      <div>
        <Label>Notes</Label>
        <textarea
          value={form.notes}
          onChange={set('notes')}
          disabled={saving}
          rows={2}
          placeholder="Inspector notes or conditions…"
          className={`${inputCls} resize-none`}
        />
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Inspection'}
        </button>
      </div>
    </form>
  );
};

// ── Inspection Row ────────────────────────────────────────────────────────────

interface InspectionRowProps {
  inspection: Inspection;
  onEditRequest: (i: Inspection) => void;
  onDeleteRequest: (id: number) => void;
}

const InspectionRow = ({ inspection, onEditRequest, onDeleteRequest }: InspectionRowProps) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold text-gray-900">{inspection.inspection_type}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${inspectionResultBadge[inspection.result]}`}>
            {inspection.result}
          </span>
        </div>
        {(inspection.permit_type || inspection.permit_number) && (
          <p className="text-xs text-gray-500 mt-0.5">
            Permit: {inspection.permit_type ?? ''}
            {inspection.permit_number ? ` · ${inspection.permit_number}` : ''}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => onEditRequest(inspection)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <span className="material-symbols-outlined text-base leading-none">edit</span>
          Edit
        </button>
        <button
          onClick={() => onDeleteRequest(inspection.id)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
        >
          <span className="material-symbols-outlined text-base leading-none">delete</span>
          Delete
        </button>
      </div>
    </div>

    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
      {inspection.inspector_name && (
        <div>
          <p className="text-xs text-gray-400">Inspector</p>
          <p className="text-xs font-medium text-gray-700">{inspection.inspector_name}</p>
        </div>
      )}
      <div>
        <p className="text-xs text-gray-400">Scheduled</p>
        <p className="text-xs font-medium text-gray-700">{fmtDate(inspection.scheduled_date)}</p>
      </div>
      <div>
        <p className="text-xs text-gray-400">Completed</p>
        <p className="text-xs font-medium text-gray-700">{fmtDate(inspection.completed_date)}</p>
      </div>
    </div>

    {inspection.notes && (
      <p className="text-xs text-gray-500 mt-2 italic">{inspection.notes}</p>
    )}
  </div>
);

// ── Main Tab ──────────────────────────────────────────────────────────────────

export const PermitsTab = ({ jobId }: PermitsTabProps) => {
  const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;

  // Permits state
  const [permits, setPermits] = useState<Permit[]>([]);
  const [permitsLoading, setPermitsLoading] = useState(true);
  const [permitsError, setPermitsError] = useState('');
  const [showPermitForm, setShowPermitForm] = useState(false);
  const [editingPermit, setEditingPermit] = useState<Permit | null>(null);
  const [deletePermitId, setDeletePermitId] = useState<number | null>(null);
  const [deletingPermit, setDeletingPermit] = useState(false);

  // Inspections state
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [inspectionsLoading, setInspectionsLoading] = useState(true);
  const [inspectionsError, setInspectionsError] = useState('');
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [editingInspection, setEditingInspection] = useState<Inspection | null>(null);
  const [deleteInspectionId, setDeleteInspectionId] = useState<number | null>(null);
  const [deletingInspection, setDeletingInspection] = useState(false);

  const loadPermits = useCallback(async () => {
    try {
      setPermitsLoading(true);
      setPermitsError('');
      const { data } = await apiClient.get(`/jobs/${numericJobId}/permits`);
      setPermits(data.data ?? data);
    } catch {
      setPermitsError('Failed to load permits.');
    } finally {
      setPermitsLoading(false);
    }
  }, [numericJobId]);

  const loadInspections = useCallback(async () => {
    try {
      setInspectionsLoading(true);
      setInspectionsError('');
      const { data } = await apiClient.get(`/jobs/${numericJobId}/inspections`);
      setInspections(data.data ?? data);
    } catch {
      setInspectionsError('Failed to load inspections.');
    } finally {
      setInspectionsLoading(false);
    }
  }, [numericJobId]);

  useEffect(() => {
    loadPermits();
    loadInspections();
  }, [loadPermits, loadInspections]);

  // Permit handlers
  const handlePermitSaved = async () => {
    setShowPermitForm(false);
    setEditingPermit(null);
    await loadPermits();
  };

  const handleDeletePermit = async () => {
    if (deletePermitId === null) return;
    setDeletingPermit(true);
    try {
      await apiClient.delete(`/jobs/${numericJobId}/permits/${deletePermitId}`);
      setDeletePermitId(null);
      await loadPermits();
    } catch {
      setPermitsError('Failed to delete permit.');
    } finally {
      setDeletingPermit(false);
    }
  };

  // Inspection handlers
  const handleInspectionSaved = async () => {
    setShowInspectionForm(false);
    setEditingInspection(null);
    await loadInspections();
  };

  const handleDeleteInspection = async () => {
    if (deleteInspectionId === null) return;
    setDeletingInspection(true);
    try {
      await apiClient.delete(`/jobs/${numericJobId}/inspections/${deleteInspectionId}`);
      setDeleteInspectionId(null);
      await loadInspections();
    } catch {
      setInspectionsError('Failed to delete inspection.');
    } finally {
      setDeletingInspection(false);
    }
  };

  const permitFormVisible = showPermitForm || editingPermit !== null;
  const inspectionFormVisible = showInspectionForm || editingInspection !== null;

  return (
    <div className="space-y-8">

      {/* ── Permits Section ───────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Permits
          </h2>
          {!permitFormVisible && (
            <button
              onClick={() => setShowPermitForm(true)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              Add Permit
            </button>
          )}
        </div>

        {permitsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{permitsError}</div>
        )}

        {showPermitForm && (
          <PermitForm
            jobId={numericJobId}
            onSaved={handlePermitSaved}
            onCancel={() => setShowPermitForm(false)}
          />
        )}

        {editingPermit && (
          <PermitForm
            initial={editingPermit}
            jobId={numericJobId}
            onSaved={handlePermitSaved}
            onCancel={() => setEditingPermit(null)}
          />
        )}

        {permitsLoading ? (
          <div className="py-12 text-center">
            <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">
              progress_activity
            </span>
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : permits.length === 0 && !permitFormVisible ? (
          <div className="py-14 bg-white rounded-xl border border-gray-200 text-center">
            <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">approval</span>
            <p className="text-sm text-gray-500 mb-4">No permits tracked for this job yet.</p>
            <button
              onClick={() => setShowPermitForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              Add First Permit
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {permits.map(permit => (
              <PermitRow
                key={permit.id}
                permit={permit}
                jobId={numericJobId}
                onEditRequest={p => { setEditingPermit(p); setShowPermitForm(false); }}
                onDeleteRequest={id => setDeletePermitId(id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <div className="border-t border-gray-200" />

      {/* ── Inspections Section ───────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Inspections
          </h2>
          {!inspectionFormVisible && (
            <button
              onClick={() => setShowInspectionForm(true)}
              className="flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              Add Inspection
            </button>
          )}
        </div>

        {inspectionsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{inspectionsError}</div>
        )}

        {showInspectionForm && (
          <InspectionForm
            jobId={numericJobId}
            permits={permits}
            onSaved={handleInspectionSaved}
            onCancel={() => setShowInspectionForm(false)}
          />
        )}

        {editingInspection && (
          <InspectionForm
            initial={editingInspection}
            jobId={numericJobId}
            permits={permits}
            onSaved={handleInspectionSaved}
            onCancel={() => setEditingInspection(null)}
          />
        )}

        {inspectionsLoading ? (
          <div className="py-12 text-center">
            <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">
              progress_activity
            </span>
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : inspections.length === 0 && !inspectionFormVisible ? (
          <div className="py-14 bg-white rounded-xl border border-gray-200 text-center">
            <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">fact_check</span>
            <p className="text-sm text-gray-500 mb-4">No inspections scheduled yet.</p>
            <button
              onClick={() => setShowInspectionForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              Schedule First Inspection
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {inspections.map(inspection => (
              <InspectionRow
                key={inspection.id}
                inspection={inspection}
                onEditRequest={i => { setEditingInspection(i); setShowInspectionForm(false); }}
                onDeleteRequest={id => setDeleteInspectionId(id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete modals */}
      {deletePermitId !== null && (
        <DeleteModal
          label="Permit"
          onConfirm={handleDeletePermit}
          onCancel={() => setDeletePermitId(null)}
          deleting={deletingPermit}
        />
      )}

      {deleteInspectionId !== null && (
        <DeleteModal
          label="Inspection"
          onConfirm={handleDeleteInspection}
          onCancel={() => setDeleteInspectionId(null)}
          deleting={deletingInspection}
        />
      )}
    </div>
  );
};
